import { findColumn } from './dataset.js';
import { evaluateMetric, groupRows } from './metrics.js';
import { DEFAULT_NUMBER_FORMAT, formatCell, formatMetricValue } from './format.js';
// ---------------------------------------------------------------------------
function columnIdSet(dataset) {
    return new Set(dataset.columns.map((c) => c.id));
}
function computeKpi(widget, data, known, locale) {
    const value = evaluateMetric(widget.metric, data.rows, known);
    const view = {
        kind: 'kpi',
        widget,
        value,
        display: formatMetricValue(value, widget.format, locale)
    };
    if (widget.caption)
        view.caption = widget.caption;
    return view;
}
function computeChart(widget, data, known, locale) {
    if (!known.has(widget.dimensionColumnId)) {
        return {
            kind: 'chart',
            widget,
            categories: [],
            series: [],
            unavailableReason: 'The column this chart groups by is missing from the data.'
        };
    }
    const groups = groupRows(data.rows, widget.dimensionColumnId);
    const dimension = findColumn(data.dataset, widget.dimensionColumnId);
    // Date dimensions sort chronologically; everything else keeps first-seen
    // order, which is what makes category order stable across refreshes.
    if (dimension?.type === 'date') {
        groups.sort((a, b) => Date.parse(a.key ?? '') - Date.parse(b.key ?? ''));
    }
    const truncated = Math.max(0, groups.length - widget.maxCategories);
    const visible = truncated > 0 ? groups.slice(0, widget.maxCategories) : groups;
    const series = widget.series.map((definition) => {
        const values = [];
        const displays = [];
        for (const group of visible) {
            const result = evaluateMetric(definition.metric, group.rows, known);
            values.push(result.available ? result.value : null);
            displays.push(formatMetricValue(result, widget.format, locale));
        }
        return { id: definition.id, label: definition.label, values, displays };
    });
    const view = {
        kind: 'chart',
        widget,
        categories: visible.map((g) => g.label),
        series
    };
    if (dimension)
        view.dimensionType = dimension.type;
    if (truncated > 0)
        view.truncatedCategories = truncated;
    if (visible.length === 0)
        view.unavailableReason = 'No data to show.';
    return view;
}
/**
 * One shared collator for text sorting.
 *
 * `String.prototype.localeCompare(other, locale, options)` builds a fresh
 * `Intl.Collator` on every call. Inside a sort that is O(n log n) collator
 * constructions — measured at 1.55s per 133k comparisons, versus 7ms for a
 * reused collator, a 200x difference. Sorting a 10,000-row table was costing
 * ~480ms, which the renderer paid again on every edit to any widget.
 */
const textCollator = new Intl.Collator('en-US', { numeric: true, sensitivity: 'variant' });
function compareCells(a, b) {
    if (a === null || a === undefined)
        return b === null || b === undefined ? 0 : 1;
    if (b === null || b === undefined)
        return -1;
    if (typeof a === 'number' && typeof b === 'number')
        return a - b;
    return textCollator.compare(String(a), String(b));
}
/** Resolves the rollup for one column: an explicit config wins; otherwise a
 *  numeric column defaults to `sum` and anything else is left blank. */
function rollupFor(config, column) {
    if (config.rollup)
        return config.rollup;
    return column.numeric ? { kind: 'sum', columnId: config.columnId } : { kind: 'none' };
}
/** Evaluates one column's rollup over a group's rows. `none` and an
 *  unavailable metric both become `null` — never `0` — so `formatCell`
 *  renders the em dash rather than a misleading real value. */
function rollupCell(rollup, groupRowsIn, known) {
    if (rollup.kind === 'none')
        return null;
    const result = evaluateMetric(rollup, groupRowsIn, known);
    return result.available ? result.value : null;
}
/**
 * The number format a group row's rollup cell should render with.
 *
 * A group row's value is not the same kind of number as the leaf column's:
 * a `ratio_of_sums` subtotal is a ratio even when the leaf column holds raw
 * hours, an `average` can land on a fraction even over an integer column,
 * and a `count` is a cardinality regardless of what column it sits under.
 * Reusing `formats[index]` verbatim made a correct-but-small subtotal
 * (3/101 = 0.0297…) round to "0.0" under a one-decimal leaf format —
 * indistinguishable from the true zero `UNAVAILABLE_DISPLAY` exists to be
 * told apart from.
 */
function groupFormatFor(rollup, columnFormat) {
    const base = columnFormat ?? DEFAULT_NUMBER_FORMAT;
    switch (rollup.kind) {
        case 'none':
        case 'sum':
            // A sum of hours is still hours: same scale as the leaf column.
            return base;
        case 'average':
            // Never round a fractional average to an integer column's 0 decimals.
            return base.decimals >= 1 ? base : { ...base, decimals: 1 };
        case 'count':
            // A cardinality, not a quantity of whatever the column measures — it
            // never inherits the column's currency or percent styling.
            return DEFAULT_NUMBER_FORMAT;
        case 'ratio_of_sums':
            // Only trust the leaf format when the author clearly intended a
            // ratio there (percent style); otherwise a ratio needs its own
            // precision, distinct from the leaf column's.
            return base.style === 'percent' ? base : { ...DEFAULT_NUMBER_FORMAT, decimals: 3 };
    }
}
/** Builds the grouped row model: one `TableRowNode` per emitted row, in
 *  `groupRows` first-encounter order, with leaf siblings sorted within their
 *  immediate parent only (never across the whole table). */
function buildGroupedCells(widget, columns, data, known) {
    const groupBy = widget.groupBy ?? [];
    const rollups = widget.columns.map((config, index) => rollupFor(config, columns[index]));
    const cells = [];
    const nodes = [];
    const walk = (rows, depth, prefix) => {
        if (depth === groupBy.length) {
            let leaves = rows;
            if (widget.sort) {
                const { columnId, direction } = widget.sort;
                const sign = direction === 'desc' ? -1 : 1;
                leaves = [...rows].sort((a, b) => sign * compareCells(a[columnId], b[columnId]));
            }
            for (const row of leaves) {
                nodes.push({ depth, kind: 'leaf', label: '', path: null, childCount: 0 });
                cells.push(columns.map((c) => row[c.columnId] ?? null));
            }
            return;
        }
        for (const group of groupRows(rows, groupBy[depth])) {
            // JSON.stringify distinguishes a `null` key from the literal string
            // "null" — `String(null)` collapsed both to the same segment, so a
            // row keyed `null` and a row keyed the string 'null' shared a path.
            // Branching on `depth === 0` (rather than on whether `prefix` happens
            // to be '') keeps an empty-string key at the root from being mistaken
            // for "no parent" by its own children, which corrupted every path
            // beneath it the same way.
            const segment = JSON.stringify(group.key);
            const path = depth === 0 ? segment : `${prefix}\u0000${segment}`;
            nodes.push({ depth, kind: 'group', label: group.label, path, childCount: group.rows.length });
            // The first column carries the label; the rest carry their rollup.
            cells.push(columns.map((_, index) => (index === 0 ? group.label : rollupCell(rollups[index], group.rows, known))));
            walk(group.rows, depth + 1, path);
        }
    };
    walk(data.rows, 0, '');
    return { cells, nodes, rollups };
}
function computeTable(widget, data, locale) {
    const columns = widget.columns.map((config) => {
        const column = findColumn(data.dataset, config.columnId);
        return {
            columnId: config.columnId,
            label: config.label ?? column?.label ?? config.columnId,
            column,
            format: config.format,
            numeric: column?.type === 'number' || column?.type === 'integer'
        };
    });
    const grouped = (widget.groupBy?.length ?? 0) > 0;
    const known = columnIdSet(data.dataset);
    let cells;
    let nodes;
    let groupRollups;
    if (grouped) {
        const built = buildGroupedCells(widget, columns, data, known);
        cells = built.cells;
        nodes = built.nodes;
        groupRollups = built.rollups;
    }
    else {
        let sorted = data.rows;
        if (widget.sort) {
            const { columnId, direction } = widget.sort;
            const sign = direction === 'desc' ? -1 : 1;
            sorted = [...sorted].sort((a, b) => sign * compareCells(a[columnId], b[columnId]));
        }
        cells = sorted.map((row) => columns.map((c) => row[c.columnId] ?? null));
    }
    // Resolve each column's format once rather than per cell.
    const formats = columns.map((column) => column.format ?? defaultFormatFor(column));
    // A group row's rollup cell needs its own format, derived from the rollup
    // kind rather than the leaf column — see `groupFormatFor`. Undefined for
    // an ungrouped table, where every row is a leaf and `formats` applies to
    // all of them, exactly as before.
    const groupFormats = groupRollups?.map((rollup, index) => groupFormatFor(rollup, formats[index]));
    const memo = new Array(cells.length);
    const formatRows = (start, end) => {
        const from = Math.max(0, Math.trunc(start));
        const to = Math.min(cells.length, Math.trunc(end));
        const out = [];
        for (let i = from; i < to; i++) {
            let formatted = memo[i];
            if (!formatted) {
                const raw = cells[i];
                const rowFormats = groupFormats && nodes?.[i]?.kind === 'group' ? groupFormats : formats;
                formatted = raw.map((value, index) => formatCell(value, rowFormats[index], locale));
                memo[i] = formatted;
            }
            out.push(formatted);
        }
        return out;
    };
    const view = {
        kind: 'table',
        widget,
        columns,
        rows: cells,
        totalRows: cells.length,
        groupDepth: widget.groupBy?.length ?? 0,
        formatRows
    };
    if (nodes)
        view.nodes = nodes;
    return view;
}
function defaultFormatFor(column) {
    if (!column?.numeric)
        return undefined;
    return column.column?.type === 'integer'
        ? DEFAULT_NUMBER_FORMAT
        : { ...DEFAULT_NUMBER_FORMAT, decimals: 1 };
}
/** Computes the view model for one widget. */
export function computeWidgetView(widget, data, locale = 'en-US') {
    const known = columnIdSet(data.dataset);
    switch (widget.type) {
        case 'kpi':
            return computeKpi(widget, data, known, locale);
        case 'chart':
            return computeChart(widget, data, known, locale);
        case 'table':
            return computeTable(widget, data, locale);
        case 'text':
            return { kind: 'text', widget, body: widget.body, authored: true };
    }
}
/** Computes every widget in a definition, keyed by widget id. */
export function computeReportViews(definition, data, locale = 'en-US') {
    const out = new Map();
    for (const widget of definition.widgets) {
        out.set(widget.id, computeWidgetView(widget, data, locale));
    }
    return out;
}
/** Text alternative for a chart, used by the accessible data table and by
 *  screen readers. Charts must never be the only way to read a number. */
export function chartAltText(view) {
    if (view.unavailableReason)
        return `Chart unavailable: ${view.unavailableReason}`;
    const seriesText = view.series
        .map((s) => {
        const points = view.categories.map((category, index) => `${category}: ${s.displays[index] ?? ''}`);
        return `${s.label} — ${points.join('; ')}`;
    })
        .join('. ');
    return `${view.widget.chartType === 'bar' ? 'Bar' : 'Line'} chart of ${view.widget.title}. ${seriesText}`;
}
const NO_COLLAPSED = new Set();
/**
 * The row indices a surface should render, in order.
 *
 * One implementation, two callers: the screen passes the collapsed set it holds
 * in local state, the print layout passes `{ maxDepth: defaultExpandedDepth }`.
 * Keeping this in core is what stops the two from drifting.
 */
export function visibleRowIndices(view, options = {}) {
    const nodes = view.nodes;
    if (!nodes)
        return view.rows.map((_, index) => index);
    const maxDepth = options.maxDepth ?? Number.POSITIVE_INFINITY;
    const collapsed = options.collapsedPaths ?? NO_COLLAPSED;
    const out = [];
    // Set to the depth of a group whose subtree is hidden; cleared on the first
    // node at or above that depth, which is where the subtree ends.
    let hideDeeperThan = null;
    for (let index = 0; index < nodes.length; index++) {
        const node = nodes[index];
        if (hideDeeperThan !== null) {
            if (node.depth > hideDeeperThan)
                continue;
            hideDeeperThan = null;
        }
        if (node.depth > maxDepth)
            continue;
        out.push(index);
        if (node.kind === 'group' && (node.depth >= maxDepth || (node.path !== null && collapsed.has(node.path)))) {
            hideDeeperThan = node.depth;
        }
    }
    return out;
}
//# sourceMappingURL=view.js.map